import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "../../../../../common/hooks/useDebounce";
import { useLazyGetProductsQuery } from "../../../../product/api/productApi";
import SearchResultList from "./SearchResultList";

interface SearchPanelProps {
  onClose?: () => void; // Ensure onClose is optional and a function
}

const SearchPanel: React.FC<SearchPanelProps> = ({ onClose }) => {
  const { t, ready: translationsReady } = useTranslation(); // Get translationsReady flag

  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const [
    triggerSearch,
    {
      data: searchResults,
      isLoading, // Still useful to differentiate initial vs subsequent
      isFetching, // Indicates *any* data fetch is happening
      isError,
    },
  ] = useLazyGetProductsQuery();

  // --- Calculate Overall Loading State ---
  const isOverallLoading = !translationsReady || isFetching;

  useEffect(() => {
    if (debouncedSearchTerm.trim().length > 2) {
      triggerSearch({ search: debouncedSearchTerm, limit: 10 });
    }
    // No need to reset results explicitly here
  }, [debouncedSearchTerm, triggerSearch]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (searchTerm.trim().length > 2) {
      triggerSearch({ search: searchTerm, limit: 10 });
    }
  };

  const handleItemClick = useCallback(() => {
    setSearchTerm("");
    if (onClose) {
      onClose(); // Call the passed onClose handler
    }
  }, [onClose]);

  const resultsData = searchResults?.products;
  const buttonText = isFetching // Use isFetching for the button text
    ? t("searchPanel.searching")
    : t("common.search");

  return (
    <div className={`search-panel ${isOverallLoading ? "is-loading" : ""}`}>
      {/* Overlay */}
      <div
        className={`search-panel__overlay ${
          isOverallLoading ? "is-active" : ""
        }`}
        aria-hidden={!isOverallLoading}
        role="status"
        aria-live="polite"
      >
        <div className="search-panel__overlay__spinner" />
      </div>

      {/* Search Form */}
      <form className="search-panel__form" onSubmit={handleSearchSubmit}>
        <input
          type="search"
          className="search-panel__input"
          placeholder={t("headerActions.searchPlaceholder")}
          aria-label={t("headerActions.searchPlaceholder")}
          value={searchTerm}
          onChange={handleInputChange}
          disabled={isOverallLoading}
        />
        <button
          type="submit"
          className="search-panel__button"
          disabled={isOverallLoading}
        >
          {buttonText}
        </button>
      </form>

      {/* Search Results Area */}
      <div className="search-panel__results">
        <SearchResultList
          results={resultsData}
          isFetching={isFetching}
          isError={isError}
          searchTerm={searchTerm} // Immediate term for length check etc.
          debouncedSearchTerm={debouncedSearchTerm} // <<<--- PASS DEBOUNCED TERM
          onItemClick={handleItemClick} // Pass the updated handler
        />
      </div>
    </div>
  );
};

export default SearchPanel;
